import { supabase } from '../lib/supabase';

/**
 * Spot Service - Supabase Integration
 * 
 * This service handles all CRUD operations for the spots table.
 * 
 * IMPORTANT: This service is ready for when the spots table is created.
 * Currently, the application uses mock data in the components.
 * Once the spots table exists in Supabase, replace mock data with these service calls.
 * 
 * Database Schema (spots table):
 * - id (uuid, primary key)
 * - name (text)
 * - description (text)
 * - location (text)
 * - latitude (float)
 * - longitude (float)
 * - location_geo (geography) - PostGIS column for spatial queries
 * - difficulty_level (text: 'Beginner', 'Intermediate', 'Advanced')
 * - parking_info (text)
 * - entry_fee (text)
 * - image_url (text)
 */

export const spotService = {
  /**
   * Fetch all spots from the database
   * @param {Object} filters - Optional filters for difficulty level
   * @returns {Promise<Array>} Array of spot objects with camelCase keys
   */
  async getAllSpots(filters = {}) {
    try {
      let query = supabase?.from('spots')?.select('*')?.order('name', { ascending: true });

      // Apply difficulty filter if provided
      if (filters?.difficulty && filters?.difficulty !== 'all') {
        const difficultyMap = {
          'Başlangıç': 'Beginner',
          'Orta': 'Intermediate',
          'İleri': 'Advanced'
        };
        query = query?.eq('difficulty_level', difficultyMap?.[filters?.difficulty]);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert snake_case to camelCase for React
      // PostGIS location_geo is automatically converted to latitude/longitude by the database
      return data?.map(spot => ({
        id: spot?.id,
        name: spot?.name,
        description: spot?.description,
        location: spot?.location,
        latitude: spot?.latitude,
        longitude: spot?.longitude,
        difficultyLevel: spot?.difficulty_level,
        parkingInfo: spot?.parking_info,
        entryFee: spot?.entry_fee,
        imageUrl: spot?.image_url,
        // CRITICAL: Include ALL database columns for edit mode
        water_condition: spot?.water_condition,
        best_time: spot?.best_time,
        facilities: spot?.facilities,
        gallery_urls: spot?.gallery_urls,
        hazards: spot?.hazards,
        accessibility: spot?.accessibility,
        // Also include snake_case versions for backward compatibility
        image_url: spot?.image_url,
        difficulty_level: spot?.difficulty_level,
        parking_info: spot?.parking_info,
        entry_fee: spot?.entry_fee,
        // Map difficulty level to Turkish
        difficulty: spot?.difficulty_level === 'Beginner' ? 'Başlangıç' :
                    spot?.difficulty_level === 'Intermediate' ? 'Orta' :
                    spot?.difficulty_level === 'Advanced' ? 'İleri' : spot?.difficulty_level,
        // Add computed fields for UI compatibility
        coordinates: {
          lat: spot?.latitude,
          lng: spot?.longitude
        },
        parking: spot?.parking_info,
        // Default values for fields that might not exist yet
        rating: 4.5,
        reviewCount: 0,
        waterCondition: spot?.water_condition || 'Sakin',
        recentVisitors: 0,
        featured: false,
        image: spot?.image_url,
        imageAlt: `${spot?.name} paddle board spot ${spot?.location}`,
        bestTime: spot?.best_time || 'Nisan - Ekim',
        gallery: spot?.gallery_urls || []
      }));
    } catch (error) {
      console.error('Error fetching spots:', error);
      throw new Error(error?.message || 'Spotlar yüklenirken bir hata oluştu');
    }
  },

  /**
   * Fetch a single spot by ID
   * @param {string|number} spotId - The spot ID
   * @returns {Promise<Object>} Single spot object with camelCase keys
   */
  async getSpotById(spotId) {
    try {
      // Validate spotId is a real UUID, not a route placeholder
      if (!spotId || spotId === ':id' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i?.test(spotId)) {
        throw new Error('Geçersiz spot ID');
      }

      const { data, error } = await supabase?.from('spots')?.select('*')?.eq('id', spotId)?.single();

      if (error) throw error;

      if (!data) {
        throw new Error('Spot bulunamadı');
      }

      // Convert snake_case to camelCase for React with NEW fields
      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        location: data?.location,
        latitude: data?.latitude,
        longitude: data?.longitude,
        difficultyLevel: data?.difficulty_level,
        parkingInfo: data?.parking_info,
        entryFee: data?.entry_fee,
        imageUrl: data?.image_url,
        // NEW FIELDS
        facilities: data?.facilities || [],
        hazards: data?.hazards || [],
        galleryUrls: data?.gallery_urls || [],
        waterCondition: data?.water_condition,
        bestTime: data?.best_time,
        accessibility: data?.accessibility,
        // Map difficulty level to Turkish
        difficulty: data?.difficulty_level === 'Beginner' ? 'Başlangıç' :
                    data?.difficulty_level === 'Intermediate' ? 'Orta' :
                    data?.difficulty_level === 'Advanced' ? 'İleri' : data?.difficulty_level,
        coordinates: {
          lat: data?.latitude,
          lng: data?.longitude
        },
        parking: data?.parking_info,
        rating: 4.5,
        reviewCount: 0,
        recentVisitors: 0,
        featured: false,
        image: data?.image_url,
        imageAlt: `${data?.name} paddle board spot ${data?.location}`,
        gallery: data?.gallery_urls || []
      };
    } catch (error) {
      console.error('Error fetching spot details:', error);
      throw new Error(error?.message || 'Spot detayları yüklenirken bir hata oluştu');
    }
  },

  /**
   * Search spots by name or location
   * @param {string} searchTerm - Search term to filter spots
   * @returns {Promise<Array>} Filtered array of spot objects
   */
  async searchSpots(searchTerm) {
    try {
      if (!searchTerm) {
        return await this.getAllSpots();
      }

      const { data, error } = await supabase?.from('spots')?.select('*')?.or(`name.ilike.%${searchTerm}%,location.ilike.%${searchTerm}%`)?.order('name', { ascending: true });

      if (error) throw error;

      // Convert snake_case to camelCase for React
      return data?.map(spot => ({
        id: spot?.id,
        name: spot?.name,
        description: spot?.description,
        location: spot?.location,
        latitude: spot?.latitude,
        longitude: spot?.longitude,
        difficultyLevel: spot?.difficulty_level,
        parkingInfo: spot?.parking_info,
        entryFee: spot?.entry_fee,
        imageUrl: spot?.image_url,
        difficulty: spot?.difficulty_level === 'Beginner' ? 'Başlangıç' :
                    spot?.difficulty_level === 'Intermediate' ? 'Orta' :
                    spot?.difficulty_level === 'Advanced' ? 'İleri' : spot?.difficulty_level,
        coordinates: {
          lat: spot?.latitude,
          lng: spot?.longitude
        },
        parking: spot?.parking_info,
        rating: 4.5,
        reviewCount: 0,
        waterCondition: 'Sakin',
        recentVisitors: 0,
        hazards: [],
        featured: false,
        image: spot?.image_url,
        imageAlt: `${spot?.name} paddle board spot ${spot?.location}`,
        facilities: [],
        bestTime: 'Nisan - Ekim',
        accessibility: 'Kolay',
        gallery: []
      }));
    } catch (error) {
      console.error('Error searching spots:', error);
      throw new Error(error?.message || 'Spot arama sırasında bir hata oluştu');
    }
  },

  /**
   * Create a new spot (Admin only)
   * @param {Object} spotData - New spot data
   * @returns {Promise<Object>} Created spot object
   */
  async createSpot(spotData) {
    try {
      const difficultyMap = {
        'Başlangıç': 'Beginner',
        'Orta': 'Intermediate',
        'İleri': 'Advanced'
      };

      const lat = parseFloat(spotData?.latitude);
      const lng = parseFloat(spotData?.longitude);

      // Prepare data for insertion with WKT format for PostGIS
      const insertData = {
        name: spotData?.name,
        description: spotData?.description,
        location: spotData?.location,
        latitude: lat,
        longitude: lng,
        // CRITICAL FIX: Use WKT format for PostGIS column
        location_geo: `POINT(${lng} ${lat})`,
        difficulty_level: difficultyMap?.[spotData?.difficulty] || spotData?.difficulty,
        parking_info: spotData?.parkingInfo,
        entry_fee: spotData?.entryFee,
        image_url: spotData?.imageUrl,
        facilities: spotData?.facilities || [],
        hazards: spotData?.hazards || [],
        gallery_urls: spotData?.galleryUrls || [],
        water_condition: spotData?.waterCondition,
        best_time: spotData?.bestTime,
        accessibility: spotData?.accessibility
      };

      const { data, error } = await supabase?.from('spots')?.insert([insertData])?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        location: data?.location,
        latitude: data?.latitude,
        longitude: data?.longitude,
        difficultyLevel: data?.difficulty_level,
        parkingInfo: data?.parking_info,
        entryFee: data?.entry_fee,
        imageUrl: data?.image_url,
        facilities: data?.facilities || [],
        hazards: data?.hazards || [],
        galleryUrls: data?.gallery_urls || [],
        waterCondition: data?.water_condition,
        bestTime: data?.best_time,
        accessibility: data?.accessibility,
        difficulty: data?.difficulty_level === 'Beginner' ? 'Başlangıç' :
                    data?.difficulty_level === 'Intermediate' ? 'Orta' :
                    data?.difficulty_level === 'Advanced' ? 'İleri' : data?.difficulty_level
      };
    } catch (error) {
      console.error('Error creating spot:', error);
      throw new Error(error?.message || 'Spot oluşturulurken bir hata oluştu');
    }
  },

  /**
   * Update an existing spot (Admin only)
   * @param {string} spotId - The spot ID to update
   * @param {Object} spotData - Updated spot data
   * @returns {Promise<Object>} Updated spot object
   */
  async updateSpot(spotId, spotData) {
    try {
      const difficultyMap = {
        'Başlangıç': 'Beginner',
        'Orta': 'Intermediate',
        'İleri': 'Advanced'
      };

      const lat = parseFloat(spotData?.latitude);
      const lng = parseFloat(spotData?.longitude);

      // Prepare data for update with WKT format for PostGIS
      const updateData = {
        name: spotData?.name,
        description: spotData?.description,
        location: spotData?.location,
        latitude: lat,
        longitude: lng,
        // CRITICAL FIX: Use WKT format for PostGIS column
        location_geo: `POINT(${lng} ${lat})`,
        difficulty_level: difficultyMap?.[spotData?.difficulty] || spotData?.difficulty,
        parking_info: spotData?.parkingInfo,
        entry_fee: spotData?.entryFee,
        image_url: spotData?.imageUrl,
        facilities: spotData?.facilities || [],
        hazards: spotData?.hazards || [],
        gallery_urls: spotData?.galleryUrls || [],
        water_condition: spotData?.waterCondition,
        best_time: spotData?.bestTime,
        accessibility: spotData?.accessibility,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('spots')?.update(updateData)?.eq('id', spotId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        description: data?.description,
        location: data?.location,
        latitude: data?.latitude,
        longitude: data?.longitude,
        difficultyLevel: data?.difficulty_level,
        parkingInfo: data?.parking_info,
        entryFee: data?.entry_fee,
        imageUrl: data?.image_url,
        facilities: data?.facilities || [],
        hazards: data?.hazards || [],
        galleryUrls: data?.gallery_urls || [],
        waterCondition: data?.water_condition,
        bestTime: data?.best_time,
        accessibility: data?.accessibility,
        difficulty: data?.difficulty_level === 'Beginner' ? 'Başlangıç' :
                    data?.difficulty_level === 'Intermediate' ? 'Orta' :
                    data?.difficulty_level === 'Advanced' ? 'İleri' : data?.difficulty_level
      };
    } catch (error) {
      console.error('Error updating spot:', error);
      throw new Error(error?.message || 'Spot güncellenirken bir hata oluştu');
    }
  },

  /**
   * Delete a spot (Admin only)
   * @param {string} spotId - The spot ID to delete
   * @returns {Promise<void>}
   */
  async deleteSpot(spotId) {
    try {
      // First, delete all related spot reviews to avoid foreign key constraint violation
      const { error: reviewsError } = await supabase
        ?.from('spot_reviews')
        ?.delete()
        ?.eq('spot_id', spotId);

      if (reviewsError) {
        console.error('Error deleting spot reviews:', reviewsError);
        throw reviewsError;
      }

      // Now delete the spot
      const { error } = await supabase?.from('spots')?.delete()?.eq('id', spotId);

      if (error) throw error;

      return { success: true, message: 'Spot başarıyla silindi' };
    } catch (error) {
      console.error('Error deleting spot:', error);
      throw new Error(error?.message || 'Spot silinirken bir hata oluştu');
    }
  },

  /**
   * Get nearby spots using PostGIS spatial query
   * @param {number} lat - Latitude of search center
   * @param {number} lng - Longitude of search center
   * @param {number} radius - Search radius in meters (default: 10000m = 10km)
   * @returns {Promise<Array>} Array of nearby spots sorted by distance
   */
  async getNearby(lat, lng, radius = 10000) {
    try {
      // Call the PostGIS RPC function created in migration
      const { data, error } = await supabase
        ?.rpc('get_nearby_spots', {
          lat: parseFloat(lat),
          long: parseFloat(lng),
          radius_meters: parseInt(radius)
        });

      if (error) throw error;

      // Convert to camelCase and add UI-friendly fields
      return data?.map(spot => ({
        id: spot?.id,
        name: spot?.name,
        description: spot?.description,
        location: spot?.location,
        latitude: spot?.latitude,
        longitude: spot?.longitude,
        difficultyLevel: spot?.difficulty_level,
        parkingInfo: spot?.parking_info,
        entryFee: spot?.entry_fee,
        imageUrl: spot?.image_url,
        waterCondition: spot?.water_condition,
        bestTime: spot?.best_time,
        accessibility: spot?.accessibility,
        facilities: spot?.facilities || [],
        hazards: spot?.hazards || [],
        galleryUrls: spot?.gallery_urls || [],
        // Distance in meters from search center
        distance: spot?.distance_meters,
        // Distance in kilometers for display
        distanceKm: (spot?.distance_meters / 1000)?.toFixed(2),
        difficulty: spot?.difficulty_level === 'Beginner' ? 'Başlangıç' :
                    spot?.difficulty_level === 'Intermediate' ? 'Orta' :
                    spot?.difficulty_level === 'Advanced' ? 'İleri' : spot?.difficulty_level,
        coordinates: {
          lat: spot?.latitude,
          lng: spot?.longitude
        },
        parking: spot?.parking_info,
        rating: 4.5,
        reviewCount: 0,
        recentVisitors: 0,
        featured: false,
        image: spot?.image_url,
        imageAlt: `${spot?.name} paddle board spot ${spot?.location}`,
        gallery: spot?.gallery_urls || [],
        createdAt: spot?.created_at,
        updatedAt: spot?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching nearby spots:', error);
      throw new Error(error?.message || 'Yakındaki spotlar yüklenirken bir hata oluştu');
    }
  }
};

export default spotService;